import { Add, AssessmentRounded } from '@mui/icons-material';
import { LoadingButton, TabContext, TabList, TabPanel } from '@mui/lab';
import { Alert, Avatar, Box, CircularProgress, Popper, Stack, Tab, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { setAutocolonSearchValue } from 'app/actions/transport';
import { setSearchValue, setSelectedItem } from 'app/actions/user';
import { IconButton, Image, InputSearch, Protect, Scrollbar } from 'app/components';
import { UserDialog, UserForm, UserList, UserReportDialog, UserTransportGroupList } from 'app/pages/User';
import UpdatePassword from 'app/pages/User/UpdatePassword/UpdatePassword';
import UserAutocolonList from 'app/pages/User/UserAutocolonList';
import UserChecklistParkScope from 'app/pages/User/UserChecklistParkScope';
import { useAsyncAction, useRoles } from 'app/services';
import { autocolunItemsLoad, divisionItemsLoad } from 'app/store/transport';
import { userDelete, userLoad, userTransportGroupLoad, userUnblock, userUpdate } from 'app/store/user';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const User = () => {
  const formRef = useRef();
  const dispatch = useDispatch();
  const isChecklist = useRoles([config.userRoles['ADMIN_CHECKLIST']]);
  const isSuperAdmin = useRoles([config.userRoles['SUPERADMIN']]);
  const { session } = useSelector((state) => state.session);
  const { items, selectedItem, searchValue } = useSelector((state) => state.user);

  const [tab, setTab] = useState('info');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const [transportGroupSearchValue, setTransportGroupSearchValue] = useState('');
  const { autocolonSearchValue } = useSelector((state) => state.transport);
  const handleSearchValueForAutocolon = (value) => {
    dispatch(setAutocolonSearchValue(value));
  };
  const { asyncAction: fetchData, isLoading: isFetchLoading } = useAsyncAction(userLoad);
  const { asyncAction: fetchGroupData, isLoading: isFetchGroupLoading } = useAsyncAction(userTransportGroupLoad);
  const { asyncAction: fetchAutocolonData, isLoading: isFetchAutocolonLoading } = useAsyncAction(autocolunItemsLoad);
  const { asyncAction: fetchDivisionData, isLoading: isFetchDivisionLoading } = useAsyncAction(divisionItemsLoad);
  const {
    asyncAction: updateSubmit,
    isLoading: isUpdateLoading,
    isSuccessful: isUpdateSuccessful,
    isError: isUpdateError,
  } = useAsyncAction(userUpdate, [selectedItem]);
  const {
    asyncAction: deleteSubmit,
    isLoading: isDeleteLoading,
    isSuccessful: isDeleteSuccessful,
    isError: isDeleteError,
  } = useAsyncAction(userDelete, [selectedItem]);
  const {
    asyncAction: unblockSubmit,
    isLoading: isUnblockLoading,
    isSuccessful: isUnblockSuccessful,
    isError: isUnblockError,
  } = useAsyncAction(userUnblock, [selectedItem]);

  const selectedIdRef = useRef(selectedItem?.id);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleReportOpen = () => {
    setReportOpen(true);
  };

  const handleReportClose = () => {
    setReportOpen(false);
  };

  const handleTabChange = (event, value) => {
    setTab(value);
  };

  const handleFormClick = () => {
    formRef.current?.handleFormSubmit();
  };

  const handleFormSubmit = (data) => {
    updateSubmit(selectedItem.id, data);
  };

  const handleDeleteClick = () => {
    deleteSubmit(selectedItem.id);
  };

  const [anchorEl, setAnchorEl] = useState(null);

  const handleTogglePopup = (event) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  const handleUnblockClick = () => {
    setAnchorEl(null);
    unblockSubmit(selectedItem.id);
  };

  const handleUserSearchChange = (value) => dispatch(setSearchValue(value));

  useEffect(() => {
    if (selectedItem && !isFetchGroupLoading && selectedIdRef.current !== selectedItem?.id) {
      fetchGroupData(selectedItem.id);
    }

    selectedIdRef.current = selectedItem?.id;
  }, [selectedItem]);

  useEffect(() => {
    fetchData();
    fetchAutocolonData();
    fetchDivisionData();
    return () => dispatch(setSelectedItem(null));
  }, []);
  return (
    <React.Fragment>
      <Stack
        direction='column'
        sx={(theme) => ({
          width: '500px',
          height: '100%',
          zIndex: '1010',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: theme.palette.divider,
        })}>
        {!isFetchLoading && items.length !== 0 && (
          <React.Fragment>
            <Stack direction='row' alignItems='center' sx={(theme) => ({ padding: theme.spacing(2) })}>
              <InputSearch placeholder='ФИО пользователя или почта' value={searchValue} onChange={handleUserSearchChange} />
              <Protect
                permissions={['create', 'delete', 'update']}
                renderPermitted={() => <IconButton onClick={handleReportOpen} endIcon={<AssessmentRounded />} />}
                renderForbidden={() => <React.Fragment />}
              />
              <IconButton onClick={handleDialogOpen} endIcon={<Add />} />
              <UserDialog open={dialogOpen} onClose={handleDialogClose} />
              <Protect
                permissions={['create', 'delete', 'update']}
                renderPermitted={() => <UserReportDialog open={reportOpen} onClose={handleReportClose} />}
                renderForbidden={() => <React.Fragment />}
              />
            </Stack>
            <Stack
              direction='column'
              alignItems='center'
              justifyContent='center'
              sx={{
                flex: '1 1 auto',
                overflow: 'hidden',
              }}>
              <UserList />
            </Stack>
          </React.Fragment>
        )}
        {!isFetchLoading && items.length === 0 && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={{
              width: '100%',
              height: '100%',
            }}>
            <Typography align='center' variant='body1' component='div'>
              Здесь пока ничего нет
            </Typography>
            <Typography align='center' color='text.secondary' variant='caption' component='div'>
              В этом списке будут отображаться пользователи системы
            </Typography>
          </Stack>
        )}
        {isFetchLoading && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={{
              width: '100%',
              height: '100%',
            }}>
            <CircularProgress size={24} />
          </Stack>
        )}
      </Stack>
      <Stack
        direction='column'
        sx={{
          height: '100%',
          flex: '1 1 auto',
          '.simplebar-content': {
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          },
        }}>
        {selectedItem && (
          <Scrollbar>
            <Stack
              direction='column'
              alignItems='center'
              sx={(theme) => ({
                maxWidth: '540px',
                margin: '0 auto',
                width: '100%',
                height: '100%',
                padding: theme.spacing(2),
              })}>
              <Stack
                direction='column'
                alignItems='center'
                sx={(theme) => ({
                  marginBottom: theme.spacing(3),
                  '.MuiAvatar-root': {
                    width: '56px',
                    height: '56px',
                    bgcolor: theme.palette.primary.main,
                  },
                  '.MuiTypography-root:first-of-type': {
                    marginTop: theme.spacing(1),
                  },
                })}>
                <Avatar>
                  <Image name={`avatar-${selectedItem.role_id}.png`} alt={`avatar-${selectedItem.role_id}`} />
                </Avatar>
                <Typography variant='body1'>{selectedItem.name}</Typography>
                {!selectedItem.active && <Alert severity='error'>Заблокирован</Alert>}
              </Stack>
              <Stack
                direction='column'
                alignItems='center'
                sx={(theme) => ({
                  width: '100%',
                  height: tab === 'group' ? '100%' : 'auto',
                  position: 'relative',
                  border: '1px solid',
                  borderRadius: theme.shape.borderRadius,
                  borderColor: theme.palette.divider,
                  bgcolor: 'background.paper',
                  '.MuiTabPanel-root': {
                    width: '100%',
                    padding: theme.spacing(2),
                  },
                })}>
                <TabContext value={tab}>
                  <TabList
                    onChange={handleTabChange}
                    TabIndicatorProps={{
                      children: <span />,
                    }}
                    sx={(theme) => ({
                      width: '100%',
                      minHeight: '44px',
                      borderBottom: '1px solid',
                      borderColor: theme.palette.divider,
                      '.MuiTab-root': {
                        fontSize: '12px',
                        minHeight: '44px',
                        padding: theme.spacing(2),
                        '&.Mui-selected': {
                          color: 'inherit',
                        },
                      },
                      '& .MuiTabs-indicator': {
                        display: 'flex',
                        justifyContent: 'center',
                        backgroundColor: 'transparent',
                      },
                      '& .MuiTabs-indicator span': {
                        width: 'calc(100% - ' + theme.spacing(4) + ')',
                        bgcolor: theme.palette.primary.main,
                        borderRadius: theme.shape.borderRadius,
                      },
                    })}>
                    <Tab value='info' label='Информация' disableRipple />
                    {isChecklist && (
                      <Tab value='autocolon' label={config.clientUi === 'voykan' ? 'Направление осмотров' : 'Автоколонны'} disableRipple />
                    )}
                    {isChecklist && <Tab value='checklistParkScope' label='Цех и бригада' disableRipple />}
                    {!isChecklist && <Tab value='group' label='Группы транспорта' disableRipple />}
                  </TabList>
                  <TabPanel value='info'>
                    <Stack
                      direction='column'
                      alignItems='center'
                      sx={{
                        width: '100%',
                        position: 'relative',
                        form: {
                          width: '100%',
                        },
                      }}>
                      <UserForm ref={formRef} defaultValues={selectedItem} onSubmit={handleFormSubmit} />
                      <Stack
                        direction='row'
                        justifyContent='flex-end'
                        sx={(theme) => ({
                          width: '100%',
                          '.MuiAlert-root': {
                            width: '100%',
                            marginTop: theme.spacing(2),
                            borderRadius: theme.shape.borderRadius,
                          },
                        })}>
                        {!isUpdateLoading && isUpdateError && (
                          <Alert severity='error'>
                            Произошла ошибка при изменении данных. Попробуйте снова или обратитесь в техническую поддержку.
                          </Alert>
                        )}
                        {!isUpdateLoading && isUpdateSuccessful && <Alert severity='success'>Данные успешно изменены.</Alert>}
                      </Stack>
                      <Stack
                        direction='row'
                        justifyContent='flex-end'
                        sx={(theme) => ({
                          width: '100%',
                          paddingTop: theme.spacing(3),
                        })}>
                        <LoadingButton variant='contained' loading={isUpdateLoading} onClick={handleFormClick} disableElevation>
                          Сохранить изменения
                        </LoadingButton>
                      </Stack>
                    </Stack>
                  </TabPanel>
                  {isChecklist ? (
                    <>
                      <TabPanel value='autocolon' style={{ padding: '0' }}>
                        {!(isFetchAutocolonLoading || isFetchDivisionLoading) && (
                          <Stack direction='row' flexDirection='column'>
                            <Stack
                              direction='row'
                              alignItems='center'
                              sx={(theme) => ({
                                padding: theme.spacing(2),
                              })}>
                              <InputSearch
                                placeholder={config.clientUi === 'voykan' ? 'Название направления осмотра' : 'Название автоколонны'}
                                value={autocolonSearchValue}
                                onChange={handleSearchValueForAutocolon}
                              />
                            </Stack>
                            <Stack>
                              <UserAutocolonList searchValue={autocolonSearchValue} />
                            </Stack>
                          </Stack>
                        )}
                        {(isFetchAutocolonLoading || isFetchDivisionLoading) && (
                          <Stack
                            direction='column'
                            alignItems='center'
                            justifyContent='center'
                            sx={(theme) => ({
                              width: '100%',
                              height: '100%',
                              padding: theme.spacing(2),
                            })}>
                            <CircularProgress size={24} />
                          </Stack>
                        )}
                      </TabPanel>
                      <TabPanel value='checklistParkScope' style={{ padding: '0' }}>
                        <UserChecklistParkScope />
                      </TabPanel>
                    </>
                  ) : (
                    <TabPanel
                      value='group'
                      style={{
                        height: '100%',
                        padding: '0',
                      }}>
                      {!isFetchGroupLoading && (
                        <Stack direction='row' flexDirection='column' sx={{ height: '100%' }}>
                          <Stack
                            direction='row'
                            alignItems='center'
                            sx={(theme) => ({
                              padding: theme.spacing(2),
                            })}>
                            <InputSearch
                              placeholder='Название группы транспорта'
                              value={transportGroupSearchValue}
                              onChange={(value) => setTransportGroupSearchValue(value)}
                            />
                          </Stack>
                          <Stack
                            direction='row'
                            flexDirection='column'
                            sx={{
                              flex: '1 1 auto',
                            }}>
                            <UserTransportGroupList searchValue={transportGroupSearchValue} />
                          </Stack>
                        </Stack>
                      )}
                      {!isFetchGroupLoading && selectedItem.transportGroups.length === 0 && (
                        <Stack
                          direction='column'
                          alignItems='center'
                          justifyContent='center'
                          sx={() => ({
                            width: '100%',
                            height: '100%',
                            flex: '1 1 auto',
                          })}>
                          <UserTransportGroupList />
                        </Stack>
                      )}
                      {isFetchGroupLoading && (
                        <Stack
                          direction='column'
                          alignItems='center'
                          justifyContent='center'
                          sx={(theme) => ({
                            width: '100%',
                            height: '100%',
                            padding: theme.spacing(2),
                          })}>
                          <CircularProgress size={24} />
                        </Stack>
                      )}
                    </TabPanel>
                  )}
                </TabContext>
              </Stack>

              {tab === 'info' && isSuperAdmin && <UpdatePassword selectedItem={selectedItem} />}

              {tab === 'info' && selectedItem.id !== session.id && (
                <Stack
                  direction='column'
                  alignItems='center'
                  sx={(theme) => ({
                    width: '100%',
                    position: 'relative',
                    marginTop: theme.spacing(3),
                    padding: theme.spacing(2),
                    border: '1px solid',
                    borderRadius: theme.shape.borderRadius,
                    borderColor: theme.palette.divider,
                    bgcolor: 'background.paper',
                    '.MuiAlert-root': {
                      marginTop: theme.spacing(0),
                      marginBottom: theme.spacing(3),
                    },
                  })}>
                  {selectedItem.active && (
                    <>
                      <Stack
                        direction='column'
                        sx={(theme) => ({
                          width: '100%',
                          paddingBottom: theme.spacing(3),
                          '.MuiTypography-h6': {
                            fontSize: '15px',
                          },
                          '.MuiTypography-caption': {
                            color: theme.palette.text.secondary,
                          },
                        })}>
                        <Typography variant='h6'>Удалить пользователя</Typography>
                        <Typography variant='caption'>
                          Можно удалить данного пользователя, но это действие необратимо. Пожалуйста, будьте уверены, что Вы хотите это
                          сделать.
                        </Typography>
                      </Stack>
                      <Stack
                        direction='row'
                        justifyContent='flex-end'
                        sx={(theme) => ({
                          width: '100%',
                          '.MuiAlert-root': {
                            width: '100%',
                            marginTop: theme.spacing(1),
                            borderRadius: theme.shape.borderRadius,
                          },
                        })}>
                        {!isDeleteLoading && isDeleteError && (
                          <Alert severity='error'>
                            Произошла ошибка при удалении данных. Попробуйте снова или обратитесь в техническую поддержку.
                          </Alert>
                        )}
                        {!isDeleteLoading && isDeleteSuccessful && <Alert severity='success'>Данные успешно удалены.</Alert>}
                      </Stack>
                      <Stack
                        direction='row'
                        justifyContent='flex-end'
                        sx={(theme) => ({
                          width: '100%',
                          paddingTop: theme.spacing(0),
                        })}>
                        <LoadingButton
                          color='error'
                          variant='contained'
                          onClick={handleDeleteClick}
                          loading={isDeleteLoading}
                          disableElevation>
                          Заблокировать пользователя
                        </LoadingButton>
                      </Stack>
                    </>
                  )}
                  {!selectedItem.active && (
                    <>
                      <Stack
                        direction='column'
                        sx={(theme) => ({
                          width: '100%',
                          paddingBottom: theme.spacing(3),
                          '.MuiTypography-h6': {
                            fontSize: '15px',
                          },
                          '.MuiTypography-caption': {
                            color: theme.palette.text.secondary,
                          },
                        })}>
                        <Typography variant='h6'>Разблокировать пользователя</Typography>
                      </Stack>
                      <Stack
                        direction='row'
                        justifyContent='flex-end'
                        sx={(theme) => ({
                          width: '100%',
                          '.MuiAlert-root': {
                            width: '100%',
                            marginTop: theme.spacing(1),
                            borderRadius: theme.shape.borderRadius,
                          },
                        })}>
                        {!isUnblockLoading && isUnblockError && (
                          <Alert severity='error'>
                            Произошла ошибка при разблокировании пользователя. Попробуйте снова или обратитесь в техническую поддержку.
                          </Alert>
                        )}
                        {!isUnblockLoading && isUnblockSuccessful && <Alert severity='success'>Пользователь успешно разблокирован .</Alert>}
                      </Stack>
                      <Stack
                        direction='row'
                        justifyContent='flex-end'
                        sx={(theme) => ({
                          width: '100%',
                          paddingTop: theme.spacing(0),
                        })}>
                        <LoadingButton
                          color='error'
                          variant='contained'
                          onClick={handleTogglePopup}
                          loading={isUnblockLoading}
                          disableElevation>
                          Разблокировать пользователя
                        </LoadingButton>

                        <Popper id={'unblock'} open={!!anchorEl} anchorEl={anchorEl}>
                          <Box
                            sx={{
                              border: 1,
                              p: 1,
                              bgcolor: 'background.paper',
                            }}>
                            <Typography
                              variant='h6'
                              sx={{
                                marginBottom: '20px',
                              }}>
                              Вы уверены?
                            </Typography>
                            <LoadingButton
                              color='error'
                              variant='contained'
                              onClick={handleTogglePopup}
                              loading={isUnblockLoading}
                              disableElevation>
                              Отмена
                            </LoadingButton>
                            <LoadingButton
                              variant='contained'
                              onClick={handleUnblockClick}
                              sx={{
                                marginLeft: '20px',
                              }}>
                              Разблокировать пользователя
                            </LoadingButton>
                          </Box>
                        </Popper>
                      </Stack>
                    </>
                  )}
                </Stack>
              )}
            </Stack>
          </Scrollbar>
        )}
        {!selectedItem && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={(theme) => ({
              height: '100%',
              padding: theme.spacing(0, 2),
              '.MuiTypography-root': {
                borderRadius: '20px',
                marginTop: '24px',
                padding: theme.spacing(0.5, 2),
                bgcolor: alpha(theme.palette.grey.main, 0.12),
              },
            })}>
            <Typography align='center' color='text.secondary' variant='caption' component='label'>
              Выберите пользователя из списка
            </Typography>
          </Stack>
        )}
      </Stack>
    </React.Fragment>
  );
};

export default User;
